import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import WithConfirm from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { OverflowChipsList } from '~/components/core/Molecules/OverflowList/Chips';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import InlineIconButton from '~/components/InlineIconButton';
import PlainTable from '~/components/PlainTable';
import type {
  EmailNotificationSuppressionConfigurationProps,
  ExcludedEmail,
  ExcludedWord,
  NotificationSuppressionData,
  NotificationSuppressionRule,
} from '~/components/SystemConfiguration/EmailConfiguration/emailConfigurationTypes';
import EmailNotificationSuppressionBuilderContainer from '~/components/SystemConfiguration/EmailConfiguration/EmailNotificationSuppressionBuilderContainer';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import Switch from '~/components/ui/atoms/Switch';
import { reportAxiosError } from '~/Utils';

import Text from '../../core/TextComponents/Text';
import AddIcon from '../../icons/AddIcon';
import PencilIcon from '../../icons/PencilIcon';
import TrashIcon from '../../icons/TrashIcon';

const BASE_PATH = '/api/v1/email_configuration/organizations';

const notificationSuppressionLoader = () => {
  return <SkeletonTable rowsCount={5} columnsCount={6} />;
};

const EmailNotificationSuppressionConfiguration: React.FC<EmailNotificationSuppressionConfigurationProps> = ({
  emailNotificationSuppressionRules,
  isLoading,
  isError,
  reloadData,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  const [isAddEmailNotificationSuppression, setIsAddEmailNotificationSuppression] = useState<boolean>(false);
  const [notificationSuppressionDataToEdit, setNotificationSuppressionDataToEdit] = useState<
    NotificationSuppressionRule | undefined
  >(undefined);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const handleDelete = async (ruleId: number) => {
    const url = `${BASE_PATH}/${organization.id}/${ruleId}/email_notification_suppression_rule`;
    setIsUpdating(true);

    try {
      await axios.delete(url);
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
    await reloadData();
  };

  const handleEditNotificationsSuppressionRule = (ruleData: NotificationSuppressionRule) => {
    setNotificationSuppressionDataToEdit(ruleData);
  };

  const handleEnableNotificationsSuppressionRule = async (
    isChecked: boolean,
    notificationsSuppressionRowData: NotificationSuppressionRule
  ) => {
    setIsUpdating(true);
    try {
      await axios.patch(
        `${BASE_PATH}/${organization.id}/${notificationsSuppressionRowData.id}/email_notification_suppression_rule/enable`,
        {
          is_enabled: isChecked,
        }
      );
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
    await reloadData();
  };

  const handleAddNotificationsSuppressionRule = () => {
    setIsAddEmailNotificationSuppression(true);
  };

  const handleNotificationSuppressionRuleSubmit = async (
    values: NotificationSuppressionData | NotificationSuppressionRule
  ) => {
    const baseUrl = `${BASE_PATH}/${organization.id}`;
    try {
      if ('id' in values && values.id !== undefined) {
        await axios.patch(`${baseUrl}/${values.id}/email_notification_suppression_rule`, values);
      } else {
        await axios.post(`${baseUrl}/email_notification_suppression_rule`, values);
      }
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const handleCloseNotificationsSuppressionDialog = () => {
    setIsAddEmailNotificationSuppression(false);
    setNotificationSuppressionDataToEdit(undefined);
  };

  const emailNotificationsSuppressionRulesColumns = [
    {
      id: 'display_name',
      label: 'Name',
    },
    {
      id: 'recipient_field',
      label: 'Recipient Field',
      specialCell: (notificationsSuppressionRowData: NotificationSuppressionRule) => {
        const fields: { key: keyof NotificationSuppressionRule; label: string }[] = [
          { key: 'check_to_field', label: 'to' },
          { key: 'check_cc_field', label: 'cc' },
          { key: 'check_bcc_field', label: 'bcc' },
        ];
        return (
          <>
            {fields.map((field) =>
              notificationsSuppressionRowData[field.key] ? (
                <Chip size="small" label={field.label} color="default" key={field.key} />
              ) : null
            )}
          </>
        );
      },
    },
    {
      id: 'excluded_emails',
      label: 'Sender',
      specialCell: (notificationsSuppressionRowData: NotificationSuppressionRule) => {
        return (
          <div className="w-3/4 min-w-[50px]">
            {notificationsSuppressionRowData?.excluded_emails ? (
              <OverflowChipsList
                items={notificationsSuppressionRowData.excluded_emails.map((emailObject: ExcludedEmail) => ({
                  id: emailObject.excluded_email,
                  chipProps: {
                    label: emailObject.excluded_email,
                    size: 'small',
                    className: 'pl-4',
                  },
                }))}
                overflowChipProps={{
                  size: 'small',
                }}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'excluded_words',
      label: 'Words Included',
      specialCell: (notificationsSuppressionRowData: NotificationSuppressionRule) => {
        return (
          <div className="w-3/4 min-w-[50px]">
            {notificationsSuppressionRowData?.excluded_words ? (
              <OverflowChipsList
                items={notificationsSuppressionRowData.excluded_words.map((wordObject: ExcludedWord) => ({
                  id: wordObject.excluded_word,
                  chipProps: {
                    label: wordObject.excluded_word,
                    size: 'small',
                    className: 'pl-4',
                  },
                }))}
                overflowChipProps={{
                  size: 'small',
                }}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'is_enabled',
      label: '',
      specialCell: (notificationsSuppressionRowData: NotificationSuppressionRule) => {
        return (
          <Switch
            onChange={(event) =>
              handleEnableNotificationsSuppressionRule(event.target.checked, notificationsSuppressionRowData)
            }
            disabled={isUpdating}
            checked={notificationsSuppressionRowData?.is_enabled || false}
          />
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      specialCell: (notificationsSuppressionRowData: NotificationSuppressionRule) => {
        return (
          <>
            <InlineIconButton
              icon={PencilIcon}
              onClick={() => handleEditNotificationsSuppressionRule(notificationsSuppressionRowData)}
              tooltipTitle="Edit"
              className="m-4 hover:text-teal-700"
              iconColor="currentColor"
              disabled={isUpdating}
            />
            <WithConfirm
              title="Delete notification suppression rule?"
              contentText={`Are you sure you want to delete ${notificationsSuppressionRowData.display_name} notification suppression rule`}
              primaryButtonName="Delete"
            >
              <InlineIconButton
                icon={TrashIcon}
                onClick={() => handleDelete(notificationsSuppressionRowData.id)}
                tooltipTitle="Delete"
                className="m-4 hover:text-teal-700"
                iconColor="currentColor"
                disabled={isUpdating}
              />
            </WithConfirm>
          </>
        );
      },
    },
  ];

  return (
    <>
      <div className="m-20">
        <LoadingSwitch isLoading={isLoading} isError={isError} LoadingComponent={notificationSuppressionLoader}>
          <CardDialog
            title="Notification suppression Rules"
            action={
              <div className="p-4">
                <Button
                  color="primary"
                  disabled={false}
                  onClick={handleAddNotificationsSuppressionRule}
                  startIcon={<AddIcon iconColor="currentColor" />}
                >
                  Add rule
                </Button>
              </div>
            }
          >
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              Suppresses the &quot;New Email&quot; notification for specific incoming emails based on defined
              criteria{' '}
            </Text>
            <Text
              variant={Text.VARIANTS.SM}
              weight={Text.WEIGHTS.REGULAR}
              colorVariant={Text.COLOR_VARIANTS.SECONDARY}
              className="mb-24"
            >
              <span className="font-bold">Note:</span> Suppression rules apply <span className="font-bold">only</span>{' '}
              to emails that are attached to a claim
            </Text>

            {emailNotificationSuppressionRules && emailNotificationSuppressionRules.length > 0 ? (
              <PlainTable
                columns={emailNotificationsSuppressionRulesColumns}
                rows={emailNotificationSuppressionRules}
              />
            ) : null}
          </CardDialog>
        </LoadingSwitch>
      </div>

      {isAddEmailNotificationSuppression || notificationSuppressionDataToEdit ? (
        <EmailNotificationSuppressionBuilderContainer
          onClose={handleCloseNotificationsSuppressionDialog}
          onNotificationSuppressionRuleSubmit={handleNotificationSuppressionRuleSubmit}
          emailNotificationSuppressionData={notificationSuppressionDataToEdit}
        />
      ) : null}
    </>
  );
};

export default EmailNotificationSuppressionConfiguration;
